import { useEffect, useState } from "react"
import Alert from "@mui/material/Alert"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useWallet } from "@solana/wallet-adapter-react"
import { isValidNickname, NICKNAME_MAX, nicknameMessage } from "./lib"

// Just the Wallet Standard bits we use (the adapter exposes the standard wallet as `adapter.wallet`)
type SignMessageFeature = { signMessage: (input: { account: unknown, message: Uint8Array }) => Promise<readonly { signedMessage: Uint8Array, signature: Uint8Array }[]> }
type StandardAdapter = { wallet?: { accounts: readonly { address: string }[], features: Record<string, unknown> } }

// Wallets may sign a modified message (Ledger wraps it in an off-chain envelope) and the adapter's signMessage
// drops those bytes, so ask the wallet directly; non-standard wallets fall back to the adapter (exact bytes)
const signForServer = async (adapter: unknown, address: string, message: Uint8Array, fallback?: (m: Uint8Array) => Promise<Uint8Array>) => {
	const standard = (adapter as StandardAdapter).wallet
	const feature = standard?.features["solana:signMessage"] as SignMessageFeature | undefined
	const account = standard?.accounts.find(a => a.address === address)
	if (feature && account) return (await feature.signMessage({ account, message }))[0]
	if (!fallback) throw new Error("Your wallet can't sign messages, so it can't set a nickname.")
	return { signedMessage: message, signature: await fallback(message) }
}
const base64 = (bytes: Uint8Array) => btoa(String.fromCharCode(...bytes))

// Netlify Function (netlify/functions/nickname.mts); not served by `yarn start`, so nicknames just don't show locally
const API = "/.netlify/functions/nickname"

export const useNickname = (number?: number) => {
	const [nickname, setNickname] = useState<string | null>(null)
	useEffect(() => {
		setNickname(null)
		if (number === undefined) return
		fetch(`${API}?number=${number}`)
			.then(r => r.ok ? r.json() : { nickname: null })
			.then(b => setNickname(b.nickname))
			.catch(() => undefined)
	}, [number])
	return [nickname, setNickname] as const
}

// Holder-only: sign a message with the wallet, the function checks the signature and that the wallet holds the ape
export const NicknameEditor = ({ number, nickname, onSaved }: { number: number, nickname: string | null, onSaved: (n: string | null) => void }) => {
	const { publicKey, signMessage, wallet } = useWallet()
	const [value, setValue] = useState(nickname ?? "")
	const [state, setState] = useState<{ saving?: boolean, error?: string, saved?: boolean }>({})
	useEffect(() => setValue(nickname ?? ""), [nickname])

	const trimmed = value.trim().replace(/\s+/g, " ")
	const valid = isValidNickname(trimmed)
	const unchanged = trimmed === (nickname ?? "")

	const save = async () => {
		if (!publicKey) return
		setState({ saving: true })
		try {
			const message = nicknameMessage(number, trimmed, Date.now())
			const signed = await signForServer(wallet?.adapter, publicKey.toBase58(), new TextEncoder().encode(message), signMessage)
			const res = await fetch(API, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ wallet: publicKey.toBase58(), message, signature: base64(signed.signature), signedMessage: base64(signed.signedMessage) }),
			})
			const body = await res.json().catch(() => ({}))
			if (!res.ok) throw new Error(body.error ?? `Couldn't save (${res.status}).`)
			onSaved(body.nickname)
			setState({ saved: true })
		} catch (e) {
			setState({ error: (e as Error).message })
		}
	}

	return (
		<Box>
			<Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: .5 }}>
				Nickname
			</Typography>
			<Box sx={{ display: "flex", gap: 1, mt: .5, alignItems: "flex-start" }}>
				<TextField size="small" value={value} placeholder="Give your ape a name" fullWidth
					onChange={e => { setValue(e.target.value); setState({}) }}
					slotProps={{ htmlInput: { maxLength: NICKNAME_MAX, "aria-label": "Nickname" } }}
					error={!valid} helperText={valid
						? "Public on this ape's page. You'll sign a message with your wallet (free, no transaction)."
						: "Letters, numbers, spaces and . ' ! ? & _ - only; start with a letter or number."} />
				<Button variant="contained" onClick={save} disabled={!valid || unchanged || state.saving} sx={{ borderRadius: 999, px: 3, flexShrink: 0 }}>
					{state.saving ? "Signing…" : trimmed || !nickname ? "Save" : "Clear"}
				</Button>
			</Box>
			{state.error && <Alert severity="error" sx={{ mt: 1 }}>{state.error}</Alert>}
			{state.saved && <Alert severity="success" sx={{ mt: 1 }}>Saved.</Alert>}
		</Box>
	)
}
